import { graphData } from './store/models/graph-data.model';
import { LoginService } from 'src/app/core/services/login';
import { MatomoAnalyticsUtils } from './core/utils/matomo-analytics.utils';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_ID, APP_INITIALIZER, isDevMode } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LangUtils } from './core/utils/lang.utils';

import { LocalStorage } from 'src/app/core/utils/local-storage';
import { DateUtils } from 'src/app/core/utils';
import { Utils } from './core/utils/utils';

// Routes
import { APP_ROUTES } from './app.routes.module';

// Modules
import { GuardsModule } from './core/guards/guards.module';

// Interceptors
import { ErrorInterceptor } from './core/services/shared/error-interceptor.service';
import { JwtInterceptor } from './core/services/shared/jwt-interceptor.service';
import { BatchIdInterceptor } from './core/services/shared/batch-id.interceptor';
import { ActionHintInterceptor } from './core/services/shared/action-hint.interceptor';

// Components
import { AppComponent } from 'src/app/app.component';
// PagesModule eliminado - era importado eagerly anulando el lazy loading de rutas
// UserDataModule se mantiene eager porque sus componentes se abren como modales desde MastersService.openSettings()
import { UserDataModule } from './pages/user-data/user-data.module';
import { TableInformeModule } from './pages/table-informe/table-informe.module';
import { TableInformeIndividualModule } from './pages/table-informe-individual/table-informe-individual.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LayoutModule } from './core/layout/layout.module';
import { BrowserModule } from '@angular/platform-browser';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { IConfig } from "ngx-mask";
import { NgxMaskModule } from "ngx-mask";
import { CoreServiceModule } from './core/services/core-services.module';
import { QuillModule } from 'ngx-quill';
import { ColorPickerModule } from 'ngx-color-picker';
// import { SocialLoginModule, AuthServiceConfig } from 'angularx-social-login';
// import { GoogleLoginProvider } from 'angularx-social-login';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { BUILD_ID } from '../environments/build-id';

import { NgxCaptchaModule } from 'ngx-captcha';
import { NgxDocViewerModule } from 'ngx-doc-viewer'

import { MatomoModule } from 'ngx-matomo-client';
import { NbThemeModule, NbLayoutModule, NbDatepickerModule, NbDialogModule, NbToastrModule, NbWindowModule, NbSpinnerModule, NbTabsetModule } from '@nebular/theme';
import { NbButtonModule, NbCardModule } from '@nebular/theme';
import { NbGlobalLogicalPosition, NbGlobalPhysicalPosition, NbGlobalPosition, NbToastrService } from '@nebular/theme';

import { userReducer } from './store/reducers/user.reducer';
import { EffectsModule } from '@ngrx/effects';
import { profilesReducer } from './store/reducers/profiles.reducer';
import { selectedProfileReducer } from './store/reducers/selected-profile.reducer';
import { graphDataReducer } from "./store/reducers/graph-data.reducer";
import { CookieService } from 'ngx-cookie-service';
import { CookieModule } from 'ngx-cookie';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { GoogleChartsModule } from 'angular-google-charts';
import { uiStateReducer } from './store/reducers/ui-state.reducer';
import { NgxPaginationModule } from 'ngx-pagination';


// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    // Cache-bust i18n JSONs.
    //   - Dev (`ng serve`): timestamp del runtime → cada reload pide JSON fresco.
    //   - Build/deploy: BUILD_ID generado por scripts/generate-build-id.js justo
    //     antes de `ng build` (commit hash o timestamp). Cada deploy emite una
    //     URL distinta — los usuarios reciben el JSON nuevo sin tener que
    //     limpiar caché aunque el browser/CDN cachee agresivamente. NO usar
    //     environment.version porque es una constante (`AppVersions.PROD`)
    //     que casi nunca se bumpea, y por eso las traducciones añadidas entre
    //     deploys quedaban invisibles.
    const cacheBust = isDevMode() ? `dev-${Date.now()}` : BUILD_ID;
    return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${cacheBust}`);
}

/**
 * Bloquea el bootstrap hasta que las traducciones del idioma inicial estén cargadas.
 * Soluciona el bug intermitente en el que los usuarios veían claves crudas
 * (ej. "EDITARCURSO.IMAGESHAPE") cuando los componentes se instanciaban antes
 * de que `translate.use()` hubiese completado la descarga del JSON.
 */
export function appTranslationsInitializer(translate: TranslateService, langUtils: LangUtils) {
    return async () => {
        translate.setDefaultLang('en');
        const lang = langUtils.getInitialLang();
        try {
            await firstValueFrom(translate.use(lang));
        } catch {
            // Si el JSON del idioma elegido falla (404, red, etc.), caemos al inglés
            // para que la app arranque igualmente sin mostrar claves crudas.
            try {
                await firstValueFrom(translate.use('en'));
            } catch { /* noop: la app arrancará, pero puede mostrar claves */ }
        }
    };
}

// let config = new AuthServiceConfig([
// 	{
// 		id: GoogleLoginProvider.PROVIDER_ID,
// 		provider: new GoogleLoginProvider(
// 			"1043386557581-d8mkegi24n815n5lnurtfau60a49sk0t.apps.googleusercontent.com",
// 			{ scope: "https://www.googleapis.com/auth/user.phonenumbers.read https://www.googleapis.com/auth/user.gender.read" }
// 		),

// 	}
// ]);

let physicalPositions = NbGlobalPhysicalPosition;
let logicalPositions = NbGlobalLogicalPosition;

// export function provideConfig() {
// 	return config;
// }

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [NgxPaginationModule,
        LayoutModule,
        APP_ROUTES,
        GuardsModule,
        BrowserAnimationsModule,
        MatDialogModule,
        MatButtonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        NgxMaskModule.forRoot({
            validation: false,
        }),
        CoreServiceModule,
        QuillModule.forRoot(),
        ColorPickerModule,
        //SocialLoginModule,
        ServiceWorkerModule.register("ngsw-worker.js", {
            enabled: environment.production,
        }),
        NgxCaptchaModule,
        NgxDocViewerModule,
        MatomoModule.forRoot({
            scriptUrl: "//matomo.salware.com/matomo.js",
            trackers: [
                {
                    trackerUrl: "http://matomo.salware.com/matomo.php",
                    siteId: environment.idMatomo,
                },
            ],

        }),
        NbThemeModule.forRoot({ name: "default" }),
        NbLayoutModule,
        NbButtonModule,
        NbSpinnerModule,
        NbCardModule,
        NbTabsetModule,
        NbDatepickerModule.forRoot(),
        NbDialogModule.forRoot({ closeOnBackdropClick: true, hasScroll: false }),
        NbToastrModule.forRoot({
            destroyByClick: true,
            duration: 6000,
            preventDuplicates: true,
            position: NbGlobalPhysicalPosition.TOP_LEFT,
        }),
        StoreModule.forRoot({
            user: userReducer,
            profiles: profilesReducer,
            selectedProfile: selectedProfileReducer,
            graphData: graphDataReducer,
            uiState: uiStateReducer,
        }, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: environment.production,
        }),
        //EffectsModule.forRoot([UserEffects]),
        BrowserModule,
        CookieModule.forRoot(),
        NbWindowModule.forRoot({
            buttons: {
                minimize: false,
                maximize: true,
                fullScreen: true,
                close: true,
            },
            closeOnBackdropClick: false,
        }),
        GoogleChartsModule,
        UserDataModule,
        TableInformeModule,
        TableInformeIndividualModule], providers: [
            MatomoAnalyticsUtils,
            DateUtils,
            Utils,
            { provide: APP_ID, useValue: 'ng-cli-universal' },
            { provide: LocalStorage, useFactory: getLocalStorage },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: ErrorInterceptor,
                multi: true,
                deps: [LoginService],
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: JwtInterceptor,
                multi: true,
                deps: [LoginService],
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: BatchIdInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: ActionHintInterceptor,
                multi: true,
            },
            // {
            // 	provide: AuthServiceConfig,
            // 	useFactory: provideConfig
            // },
            CookieService,
            NgbActiveModal,
            DatePipe,
            provideHttpClient(withInterceptorsFromDi()),
            {
                provide: APP_INITIALIZER,
                useFactory: appTranslationsInitializer,
                deps: [TranslateService, LangUtils],
                multi: true,
            },
        ]
})
export class AppModule { }

export function getLocalStorage() {
    return window.localStorage;
}


